import React, { Children } from 'react'
import { Contexts, Types } from '../types'
import _ from 'lodash'
import { useDebug } from '../hooks'

const initialState: any = {
    id: 0, // number
    name: '', // string
    description: undefined, // string|undefined if not set  
    permalink: null,
    images: {
        feature: undefined,
        gallery: []
    }, // string(imageURL)|string[(imageURLsr)]| undefined >> config.defaultImage
    gallery: undefined, // undefined if any 
    type: 'bundle', // string
    stockStatus: 'outofstock', // instock|outofstock, thats question
    manageStock: true, // boolean
    taxClass: 'standard',  // string 
    meta: { // Addditional meta
        deliveryTime: undefined, // undefined if not set
    },
    price: undefined, // undefined if type IN [variable]
    quantity: {
        min: 1,
        max: undefined,
        selected: 1
    }, // undefined if manageStock == false || isVariable
    purchasable: true,
    available: true,
    items: [],
    saleRatio: 1, 
    selected: {} // [id]: number: {quantity: number, variation: number|null}
} 

export const BundleProductContext = React.createContext<any>({}) 

const BundleProductProvider: React.FC<any> = ({ children, config, translations, product, onAddToCart: handleAddToCart }) => {    

    useDebug(`BundleProductProvider [${product.name}}]`);

    const [store, updateStore] = React.useState<any>({...initialState});

    const setState = (key: string, value: any) => {
        if (!(key in initialState)) throw Error(`Key ${key} is not valid state.`);
        if ( store[key] === value ) return;
        updateStore((prevStore: any) => ({ ...prevStore, [key]: value instanceof Function ? value(prevStore[key]) : value }))
    }

    const handleVariationSelect = React.useCallback(({id, variation, quantity}: any) => {
        // {[id]: {variation, quantity: quantity || 1}}
        setState('selected', ( (prevState: any ) =>  _.merge({...prevState}, {[id]: {variation, quantity: quantity || 1}})))
    }, []);

    const onAddToCart = React.useCallback(() => {
        handleAddToCart({...productData});
    }, [store])

    const getSelectedBundledItemMeta = React.useCallback(([bundledItemID, bundledItemProduct]: any): any  => {
        
        const bundledItemMeta: any = {
            product: bundledItemProduct.id,
            quantity: bundledItemProduct?.quantity?.selected ?? bundledItemProduct?.quantity?.min ?? config?.general?.quantity.min,
        }

        // TODO: May it should be pre-selected here
        if ( bundledItemProduct.type === 'variable' ) {
            bundledItemMeta.variation = null;
        }

        return [bundledItemID, bundledItemMeta];
        
    }, [config?.general?.quantity?.min])

    const setInitiallySelectedItems = () => {
        setState('selected', Object.fromEntries(
            Object.entries(product.items).map(getSelectedBundledItemMeta)
        ));
    }

    const getItemRegularPrice = React.useCallback(({product: productID, id: itemID, quantity, variation}: {product: number, id: number, quantity: number, variation?: number | null,}) => {

        const itemProduct: any = ! _.isUndefined(variation) ? ( _.isNumber(variation) ? product.items[itemID]?.variations[variation] : null): product.items[itemID];
        return itemProduct ? ( itemProduct.price?.sale ?? itemProduct?.price?.regular ) * quantity : 0;
        //return itemProduct ? ( itemProduct.price?.sale ?? itemProduct?.price?.regular ) * itemProduct.quantity?.selected ?? itemProduct.quantity?.min ?? config.general.quantity.min : 0;

    }, [product.items]); // , config.general.quantity.min

    const getItemsRegularPrice = React.useCallback((items: any, selectedItems: any) => {
        return Object.entries(selectedItems).reduce((totalPrice: number, [id, meta]: any) => totalPrice + getItemRegularPrice({...meta, id, items}), 0); 
    }, [getItemRegularPrice])

    const bundledItemsRegularPrice      = React.useMemo(() => getItemsRegularPrice(product.items, store.selected), [product.items, store.selected, getItemsRegularPrice]);
    const bundledItemsSaleRatio         = React.useMemo(() => bundledItemsRegularPrice > 0 ? (product.price?.sale ?? product.price?.regular) / bundledItemsRegularPrice : 1, [product.price, bundledItemsRegularPrice,]);
    
    const productData = React.useMemo(() => _.merge(
        store, 
        {...product, onAddToCart, onVariationSelect: handleVariationSelect},
        product.type === 'variable' ? {onVariationSelect: handleVariationSelect} : {},
        { saleRatio: bundledItemsSaleRatio, price: {...product.price, regular: bundledItemsRegularPrice, sale: product?.price?.sale || product?.price?.regular} }
    ), [product, store, bundledItemsSaleRatio, bundledItemsRegularPrice, handleVariationSelect, onAddToCart]);

    React.useEffect(setInitiallySelectedItems, []);
 
    return (
        <BundleProductContext.Provider
            value={{
                ...productData,
                config,
                translations,
                setState,
            }}
            children={!_.isEmpty(product) ? children : null }
        />
    ) 

}

export default BundleProductProvider;